import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { useAirportNames } from '@/hooks/useAirportNames';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { Steps, createSteps, updateStepStatus } from '@/components/ui';
import { AirportAutocomplete } from '@/components/ui/Autocomplete/AirportAutocomplete';
import { PageContainer } from '@/components/ui/PageContainer';
import { PageHeader } from '@/components/ui/PageHeader';
import {
	FlightManifestService,
	type FlightManifestDTO,
	type FlightManifestFileDTO,
	type ProcessingStatusDTO,
	type RecordWithSourceDTO,
	type AdvisoryDTO,
} from '@/services/FlightManifestService';
import { EventsService, type EventWithClientDTO } from '@/services/EventsService';
import { useNotificationStore } from '@/stores/notificationStore';
import { ArrowRight, ArrowLeft, Upload, FileText, CheckCircle, AlertTriangle, Loader2, X, Users, Plane, Pencil, Eye, Trash2, Search, Info } from 'lucide-react';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog/ConfirmDialog';
import { FileViewerModal, type FileViewerFile } from '@/components/ui/FileViewer/FileViewerModal';
import {
	type FlightLeg, type BookingGroup, type PassengerGroup, type EditingCell, type NewLegData,
	formatDate,
	groupRecordsByPassenger,
	PassengerGroupRows,
} from './shared/ManifestTableComponents';

interface Props {
	onComplete: () => void;
	onCancel: () => void;
	preSelectedEventId?: string;
}

type WizardStep = 'event' | 'upload' | 'processing' | 'review_data' | 'review_approve';

interface PendingFile {
	file: File;
	status: 'pending' | 'uploading' | 'done' | 'error';
	uploadedFile?: FlightManifestFileDTO;
	error?: string;
}

export function CreateManifestWizard({ onComplete, onCancel, preSelectedEventId }: Props) {
	const navigate = useNavigate();
	const manifestService = useMemo(() => new FlightManifestService(), []);
	const eventsService = useMemo(() => new EventsService(), []);
	const notifyError = useNotificationStore(s => s.error);
	const notifySuccess = useNotificationStore(s => s.success);

	// Always start on the first step — even when the event is pre-selected we must
	// collect the destination airport(s) before creating/processing the manifest.
	const initialStep: WizardStep = 'event';
	const [currentStep, setCurrentStepRaw] = useState<WizardStep>(initialStep);
	const [loading, setLoading] = useState(false);
	const [autoAdvanced, setAutoAdvanced] = useState(false);

	// Push browser history on step changes so back button navigates wizard steps
	const setCurrentStep = useCallback((step: WizardStep) => {
		setCurrentStepRaw(step);
		window.history.pushState({ wizardStep: step }, '');
	}, []);

	// Seed initial history entry
	useEffect(() => {
		window.history.replaceState({ wizardStep: initialStep }, '');
	}, []);

	// Listen for browser back button
	useEffect(() => {
		const handlePopState = (e: PopStateEvent) => {
			if (e.state?.wizardStep) {
				setCurrentStepRaw(e.state.wizardStep);
			} else {
				// No wizard state — user went back past the wizard, trigger cancel
				onCancel();
			}
		};
		window.addEventListener('popstate', handlePopState);
		return () => window.removeEventListener('popstate', handlePopState);
	}, [onCancel]);

	// Step 1: Event selection
	const [events, setEvents] = useState<EventWithClientDTO[]>([]);
	const [selectedEvent, setSelectedEvent] = useState<EventWithClientDTO | null>(null);
	const [manifestName, setManifestName] = useState('');
	// Destination airport anchor — the code(s) every attendee flies into/out of for this program.
	const [destinationAirports, setDestinationAirports] = useState<string[]>([]);
	const [destInputKey, setDestInputKey] = useState(0);

	// Step 2: Upload
	const [manifest, setManifest] = useState<FlightManifestDTO | null>(null);
	const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
	const [uploading, setUploading] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);

	// Step 3: Processing
	const [processingStatus, setProcessingStatus] = useState<ProcessingStatusDTO | null>(null);
	const [isProcessing, setIsProcessing] = useState(false);
	const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

	// Step 5: Review
	const [reviewRecords, setReviewRecords] = useState<RecordWithSourceDTO[]>([]);
	const [allBatchRecords, setAllBatchRecords] = useState<RecordWithSourceDTO[]>([]);
	const [reviewLoading, setReviewLoading] = useState(false);
	const [approving, setApproving] = useState(false);

	// Review Data step (editable table)
	const [reviewDataRecords, setReviewDataRecords] = useState<RecordWithSourceDTO[]>([]);
	const [rdAllBatchRecords, setRdAllBatchRecords] = useState<RecordWithSourceDTO[]>([]);
	const [reviewDataLoading, setReviewDataLoading] = useState(false);
	const [rdEditingCell, setRdEditingCell] = useState<EditingCell | null>(null);
	const [rdEditValue, setRdEditValue] = useState('');
	const [rdSaving, setRdSaving] = useState(false);
	const [rdAddingLeg, setRdAddingLeg] = useState<{ recordId: string } | null>(null);
	const [rdNewLeg, setRdNewLeg] = useState<NewLegData>({ departure_airport: '', arrival_airport: '', flight_number: '', date: '', departure_time: '', arrival_time: '', direction: 'outbound' });
	const [rdDeleteConfirm, setRdDeleteConfirm] = useState<{
		type: 'passenger' | 'booking' | 'leg';
		label: string;
		recordIds: string[];
		legInfo?: { recordId: string; allFlightsIndex: number };
	} | null>(null);
	const [rdDeleting, setRdDeleting] = useState(false);
	const [rdFileLoading, setRdFileLoading] = useState<string | null>(null);
	const [rdViewingFile, setRdViewingFile] = useState<FileViewerFile | null>(null);

	// Update comparison state
	const [updateComparisons, setUpdateComparisons] = useState<import('@/services/FlightManifestService').UpdateComparisonDTO[]>([]);
	const [acceptedLegs, setAcceptedLegs] = useState<Record<string, Set<number>>>({}); // pendingRecordId -> set of accepted leg indices

	// Duplicate detection (Review Data step — LLM call 1)
	const [duplicateAdvisories, setDuplicateAdvisories] = useState<AdvisoryDTO[]>([]);
	const [duplicatesLoading, setDuplicatesLoading] = useState(false);

	// Anomaly detection (Review & Approve step — LLM call 2)
	const [advisories, setAdvisories] = useState<AdvisoryDTO[]>([]);
	const [advisoriesLoading, setAdvisoriesLoading] = useState(false);

	// PDF viewer for Review & Approve step
	const [raViewingFile, setRaViewingFile] = useState<FileViewerFile | null>(null);
	const [raFileLoading, setRaFileLoading] = useState<string | null>(null);

	const stepLabels = preSelectedEventId
		? ['Destination', 'Upload PDFs', 'Process & Extract', 'Review Data', 'Review & Approve']
		: ['Event & Destination', 'Upload PDFs', 'Process & Extract', 'Review Data', 'Review & Approve'];
	const stepKeys = ['event', 'upload', 'processing', 'review_data', 'review_approve'];
	const currentStepIndex = stepKeys.indexOf(currentStep);
	const steps = updateStepStatus(createSteps(stepLabels), currentStepIndex);

	useEffect(() => {
		loadEvents();
		return () => { if (pollRef.current) clearInterval(pollRef.current); };
	}, []);

	// Pre-select the event when opened from an event page — but still require the
	// destination airport(s) on the first step before the manifest is created.
	useEffect(() => {
		if (preSelectedEventId && events.length > 0 && !autoAdvanced) {
			setAutoAdvanced(true);
			const match = events.find(e => e.id === preSelectedEventId);
			if (match) {
				setSelectedEvent(match);
				setManifestName(`${match.client_name} - ${match.name} Flight Manifests`);
			}
		}
	}, [events, preSelectedEventId]);

	async function loadEvents() {
		setLoading(true);
		try {
			const data = await eventsService.listAll();
			setEvents(data);
		} catch (e: any) {
			notifyError(e?.message || 'Failed to load events');
		} finally {
			setLoading(false);
		}
	}

	const handleEventSelect = (event: EventWithClientDTO) => {
		setSelectedEvent(event);
		if (!manifestName.trim()) {
			setManifestName(`${event.client_name} - ${event.name} Flight Manifests`);
		}
	};

	const addDestinationAirport = (code: string) => {
		const c = (code || '').toUpperCase().trim();
		if (!/^[A-Z]{3}$/.test(c)) return;
		setDestinationAirports((prev) => (prev.includes(c) ? prev : [...prev, c]));
		setDestInputKey((k) => k + 1); // remount autocomplete to clear it
	};
	const removeDestinationAirport = (code: string) => {
		setDestinationAirports((prev) => prev.filter((c) => c !== code));
	};

	const handleCreateAndAdvance = async () => {
		if (!selectedEvent || !manifestName.trim() || destinationAirports.length === 0) return;
		if (manifest) { setCurrentStep('upload'); return; } // already created — don't duplicate
		setLoading(true);
		try {
			const created = await manifestService.create(selectedEvent.id, manifestName, destinationAirports);
			setManifest(created);
			setCurrentStep('upload');
		} catch (e: any) {
			notifyError(e?.message || 'Failed to create manifest');
		} finally {
			setLoading(false);
		}
	};

	// ── File handling ──
	const handleFilesSelected = (fileList: FileList | null) => {
		if (!fileList) return;
		const newFiles: PendingFile[] = Array.from(fileList)
			.filter(f => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'))
			.map(f => ({ file: f, status: 'pending' as const }));
		if (newFiles.length === 0) {
			notifyError('Please select PDF files only');
			return;
		}
		setPendingFiles(prev => [...prev, ...newFiles]);
	};

	const removePendingFile = (index: number) => {
		setPendingFiles(prev => prev.filter((_, i) => i !== index));
	};

	const handleDrop = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		handleFilesSelected(e.dataTransfer.files);
	}, []);

	const handleDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault();
	}, []);

	const handleUploadAll = async () => {
		if (!manifest || pendingFiles.length === 0) return;
		setUploading(true);

		const updated = [...pendingFiles];
		for (let i = 0; i < updated.length; i++) {
			if (updated[i].status !== 'pending') continue;
			updated[i] = { ...updated[i], status: 'uploading' };
			setPendingFiles([...updated]);

			try {
				const uploadedFile = await manifestService.uploadFile(manifest.id, updated[i].file);
				updated[i] = { ...updated[i], status: 'done', uploadedFile };
			} catch (e: any) {
				updated[i] = { ...updated[i], status: 'error', error: e?.message || 'Upload failed' };
			}
			setPendingFiles([...updated]);
		}

		setUploading(false);
		const doneCount = updated.filter(f => f.status === 'done').length;
		if (doneCount > 0) {
			notifySuccess(`${doneCount} file(s) uploaded successfully`);
		}
	};

	const uploadedCount = pendingFiles.filter(f => f.status === 'done').length;
	const canProceedToProcessing = uploadedCount > 0;

	// ── Processing ──
	const handleStartProcessing = async () => {
		if (!manifest) return;
		setIsProcessing(true);
		setCurrentStep('processing');

		try {
			await manifestService.process(manifest.id);
		} catch (e: any) {
			notifyError(e?.message || 'Failed to start processing');
			setIsProcessing(false);
			return;
		}

		// Start polling — keep polling until manifestStatus is 'review' or 'failed'
		// (not just until files are extracted, since anomaly detection runs after extraction)
		pollRef.current = setInterval(async () => {
			try {
				const status = await manifestService.getStatus(manifest.id);
				setProcessingStatus(status);

				// Stop polling when manifest reaches a terminal state (review = extraction + anomaly detection done)
				if (status.manifestStatus === 'review' || status.manifestStatus === 'failed') {
					if (pollRef.current) clearInterval(pollRef.current);
					pollRef.current = null;
					setIsProcessing(false);
				}
			} catch {
				// Ignore poll errors
			}
		}, 2500);
	};

	const processingDone = processingStatus
		&& processingStatus.manifestStatus === 'review';

	// ── Review step ──
	const eventId = selectedEvent?.id || preSelectedEventId;

	const handleAdvanceFromProcessing = async () => {
		if (!eventId) return;
		// Always go to review_data step first
		await loadReviewDataStep();
	};

	const loadReviewDataStep = async () => {
		if (!eventId) return;
		setReviewDataLoading(true);
		try {
			const eventData = await manifestService.getAllByEvent(eventId, manifest?.id);
			const batchRecords = manifest
				? eventData.records.filter((r: any) => r.manifest_id === manifest.id)
				: eventData.records;
			setRdAllBatchRecords(batchRecords);

			// Separate new records from pending updates
			const pendingUpdates = batchRecords.filter((r: any) => r.updates_record_id);
			const newRecords = batchRecords.filter((r: any) => !r.updates_record_id);
			setReviewDataRecords(newRecords);

			// Fetch update comparisons if there are pending updates
			if (manifest && pendingUpdates.length > 0) {
				const comparisons = await manifestService.getUpdateComparisons(manifest.id);
				setUpdateComparisons(comparisons);
				// Default: accept all changed/new legs
				const defaults: Record<string, Set<number>> = {};
				for (const comp of comparisons) {
					const accepted = new Set<number>();
					comp.legs.forEach((leg, i) => {
						if (leg.type === 'updated' || leg.type === 'new') accepted.add(i);
					});
					defaults[comp.pendingRecordId] = accepted;
				}
				setAcceptedLegs(defaults);
			} else {
				setUpdateComparisons([]);
				setAcceptedLegs({});
			}

			setCurrentStep('review_data');

			// Fire duplicate detection in background (LLM call 1)
			if (manifest && batchRecords.length > 0) {
				setDuplicatesLoading(true);
				manifestService.detectDuplicates(manifest.id)
					.then(results => setDuplicateAdvisories(results))
					.catch(e => console.warn('Duplicate detection failed:', e))
					.finally(() => setDuplicatesLoading(false));
			}
		} catch (e: any) {
			notifyError(e?.message || 'Failed to load extracted data');
		} finally {
			setReviewDataLoading(false);
		}
	};

	// ── Review Data editing handlers ──
	const rdStartEdit = (recordId: string, field: string, currentValue: string, legSource?: EditingCell['legSource'], allFlightsIndex?: number) => {
		setRdEditingCell({ recordId, field, legSource, allFlightsIndex });
		setRdEditValue(currentValue || '');
	};
	const rdCancelEdit = () => { setRdEditingCell(null); setRdEditValue(''); };

	const rdSaveEdit = async () => {
		if (!rdEditingCell || !eventId) return;
		setRdSaving(true);
		try {
			const record = reviewDataRecords.find(r => r.id === rdEditingCell.recordId);
			if (!record) throw new Error('Record not found');
			let updates: Record<string, any> = {};

			if (rdEditingCell.legSource === 'all_flights' && rdEditingCell.allFlightsIndex !== undefined) {
				const allFlights = [...(record.all_flights || [])];
				const idx = rdEditingCell.allFlightsIndex;
				if (allFlights[idx]) {
					const fieldMap: Record<string, string> = { departure_airport: 'departure_airport', arrival_airport: 'arrival_airport', flight_number: 'flight_number', date: 'date', departure_time: 'departure_time', arrival_time: 'arrival_time' };
					const legField = fieldMap[rdEditingCell.field];
					if (legField) {
						allFlights[idx] = { ...allFlights[idx], [legField]: rdEditValue || null };
						updates = { all_flights: allFlights };
					}
				}
			} else if (rdEditingCell.legSource === 'primary_outbound') {
				const fieldMap: Record<string, string> = { departure_airport: 'outbound_departure_airport', arrival_airport: 'outbound_arrival_airport', flight_number: 'outbound_flight_number', date: 'outbound_departure_date', departure_time: 'outbound_departure_time', arrival_time: 'outbound_arrival_time' };
				const dbField = fieldMap[rdEditingCell.field];
				if (dbField) updates[dbField] = rdEditValue || null;
			} else if (rdEditingCell.legSource === 'primary_return') {
				const fieldMap: Record<string, string> = { departure_airport: 'return_departure_airport', arrival_airport: 'return_arrival_airport', flight_number: 'return_flight_number', date: 'return_flight_date', departure_time: 'return_departure_time', arrival_time: 'return_arrival_time' };
				const dbField = fieldMap[rdEditingCell.field];
				if (dbField) updates[dbField] = rdEditValue || null;
			} else if (rdEditingCell.field === 'passenger_name_combo') {
				const [first, last] = rdEditValue.split('||');
				updates.passenger_first_name = first?.trim() || null;
				updates.passenger_last_name = last?.trim() || null;
				updates.passenger_name = [first?.trim(), last?.trim()].filter(Boolean).join(' ') || null;
			} else {
				updates[rdEditingCell.field] = rdEditValue || null;
			}

			const res = await apiFetch(`/api/flight-manifests/by-event/${eventId}/records/${rdEditingCell.recordId}`, {
				method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates),
			});
			if (!res.ok) throw new Error('Failed to update');
			notifySuccess('Record updated');
			setReviewDataRecords(prev => prev.map(r => r.id === rdEditingCell!.recordId ? { ...r, ...updates } : r));
			rdCancelEdit();
		} catch (e: any) { notifyError(e?.message || 'Failed to update'); }
		finally { setRdSaving(false); }
	};

	const rdStartAddLeg = (recordId: string) => {
		setRdAddingLeg({ recordId });
		setRdNewLeg({ departure_airport: '', arrival_airport: '', flight_number: '', date: '', departure_time: '', arrival_time: '', direction: 'outbound' });
	};
	const rdCancelAddLeg = () => setRdAddingLeg(null);

	const rdSaveNewLeg = async () => {
		if (!rdAddingLeg || !eventId) return;
		const { recordId } = rdAddingLeg;
		const record = reviewDataRecords.find(r => r.id === recordId);
		if (!record) return;
		setRdSaving(true);
		try {
			const entry = {
				departure_airport: rdNewLeg.departure_airport || '',
				arrival_airport: rdNewLeg.arrival_airport || '',
				flight_number: rdNewLeg.flight_number || null,
				date: rdNewLeg.date || '',
				departure_time: rdNewLeg.departure_time || null,
				arrival_time: rdNewLeg.arrival_time || null,
				direction: rdNewLeg.direction,
			};
			let allFlights = record.all_flights ? [...record.all_flights] : [];
			if (allFlights.length === 0) {
				if (record.outbound_departure_airport || record.outbound_departure_date) {
					allFlights.push({ departure_airport: record.outbound_departure_airport || '', arrival_airport: record.outbound_arrival_airport || '', flight_number: record.outbound_flight_number || null, date: record.outbound_departure_date?.slice(0, 10) || '', departure_time: record.outbound_departure_time || null, arrival_time: record.outbound_arrival_time || null, direction: 'outbound' as const });
				}
				if (record.return_departure_airport || record.return_flight_date) {
					allFlights.push({ departure_airport: record.return_departure_airport || '', arrival_airport: record.return_arrival_airport || '', flight_number: record.return_flight_number || null, date: record.return_flight_date?.slice(0, 10) || '', departure_time: record.return_departure_time || null, arrival_time: record.return_arrival_time || null, direction: 'return' as const });
				}
			}
			allFlights.push(entry);
			allFlights.sort((a, b) => {
				const da = (a.date || '').slice(0, 10);
				const db = (b.date || '').slice(0, 10);
				if (da !== db) return da.localeCompare(db);
				return (a.departure_time || '').localeCompare(b.departure_time || '');
			});
			const res = await apiFetch(`/api/flight-manifests/by-event/${eventId}/records/${recordId}`, {
				method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ all_flights: allFlights }),
			});
			if (!res.ok) throw new Error('Failed to add leg');
			setReviewDataRecords(prev => prev.map(r => r.id === recordId ? { ...r, all_flights: allFlights } : r));
			notifySuccess('Flight leg added');
			setRdAddingLeg(null);
		} catch (e: any) { notifyError(e?.message || 'Failed to add leg'); }
		finally { setRdSaving(false); }
	};

	const rdRequestDeletePassenger = (group: PassengerGroup) => {
		const ids = group.bookings.map(b => b.recordId);
		setRdDeleteConfirm({ type: 'passenger', label: group.name || 'this passenger', recordIds: ids });
	};
	const rdRequestDeleteBooking = (booking: BookingGroup) => {
		setRdDeleteConfirm({ type: 'booking', label: `booking ${booking.pnr.startsWith('record-') ? '' : booking.pnr}`.trim() || 'this booking', recordIds: [booking.recordId] });
	};
	const rdRequestDeleteLeg = (booking: BookingGroup, leg: FlightLeg) => {
		if (leg.source !== 'all_flights' || leg.allFlightsIndex === undefined) {
			setRdDeleteConfirm({ type: 'booking', label: 'this booking record', recordIds: [booking.recordId] });
			return;
		}
		const route = [leg.departureAirport, leg.arrivalAirport].filter(Boolean).join(' → ') || 'this leg';
		setRdDeleteConfirm({ type: 'leg', label: route, recordIds: [booking.recordId], legInfo: { recordId: booking.recordId, allFlightsIndex: leg.allFlightsIndex } });
	};

	const rdHandleDeleteConfirm = async () => {
		if (!rdDeleteConfirm || !eventId) return;
		setRdDeleting(true);
		try {
			if (rdDeleteConfirm.type === 'leg' && rdDeleteConfirm.legInfo) {
				const { recordId, allFlightsIndex } = rdDeleteConfirm.legInfo;
				const record = reviewDataRecords.find(r => r.id === recordId);
				if (!record) throw new Error('Record not found');
				const allFlights = [...(record.all_flights || [])];
				allFlights.splice(allFlightsIndex, 1);
				const res = await apiFetch(`/api/flight-manifests/by-event/${eventId}/records/${recordId}`, {
					method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ all_flights: allFlights }),
				});
				if (!res.ok) throw new Error('Failed to remove leg');
				setReviewDataRecords(prev => prev.map(r => r.id === recordId ? { ...r, all_flights: allFlights } : r));
				notifySuccess('Flight leg removed');
			} else {
				await manifestService.bulkDeleteRecords(eventId, rdDeleteConfirm.recordIds);
				setReviewDataRecords(prev => prev.filter(r => !rdDeleteConfirm.recordIds.includes(r.id)));
				notifySuccess(rdDeleteConfirm.type === 'passenger' ? 'Passenger removed' : 'Booking removed');
			}
		} catch (e: any) { notifyError(e?.message || 'Failed to delete'); }
		finally { setRdDeleting(false); setRdDeleteConfirm(null); }
	};

	const rdHandleViewSource = async (fileId: string, filename: string) => {
		setRdFileLoading(fileId);
		try {
			const { url } = await manifestService.getFileViewUrl(fileId);
			setRdViewingFile({ url, name: filename, mimeType: 'application/pdf' });
		} catch (e: any) { notifyError(e?.message || 'Failed to load file'); }
		finally { setRdFileLoading(null); }
	};

	const handleAdvanceFromReviewData = async () => {
		if (!manifest || !eventId) return;
		setAdvisoriesLoading(true);
		setReviewLoading(true);
		try {
			// Load review records first, then fire anomaly detection (LLM call 2)
			const eventData = await manifestService.getAllByEvent(eventId, manifest.id);
			const batchAll = eventData.records.filter((r: any) => r.manifest_id === manifest.id);
			setAllBatchRecords(batchAll);
			setReviewRecords(batchAll);
			setCurrentStep('review_approve');
			setReviewLoading(false);

			// Fire anomaly detection in parallel (flight logic only, no attendees)
			const anomalyResults = await manifestService.detectAnomalies(manifest.id);
			setAdvisories(anomalyResults);
		} catch (e: any) {
			notifyError(e?.message || 'Failed to load review data');
		} finally {
			setAdvisoriesLoading(false);
			setReviewLoading(false);
		}
	};

	const handleReAnalyze = async () => {
		if (!manifest) return;
		setAdvisoriesLoading(true);
		try {
			const results = await manifestService.detectAnomalies(manifest.id);
			setAdvisories(results);
			notifySuccess('Anomaly analysis updated');
		} catch (e: any) {
			notifyError(e?.message || 'Failed to analyze records');
		} finally {
			setAdvisoriesLoading(false);
		}
	};

	const raHandleViewSource = async (fileId: string, filename: string) => {
		setRaFileLoading(fileId);
		try {
			const { url } = await manifestService.getFileViewUrl(fileId);
			setRaViewingFile({ url, name: filename, mimeType: 'application/pdf' });
		} catch (e: any) { notifyError(e?.message || 'Failed to load file'); }
		finally { setRaFileLoading(null); }
	};

	const buildUpdateMerges = () => {
		return updateComparisons.map(comp => ({
			pendingRecordId: comp.pendingRecordId,
			existingRecordId: comp.existingRecordId,
			acceptedLegIndices: [...(acceptedLegs[comp.pendingRecordId] || [])],
		}));
	};

	const handleApproveAndFinish = async () => {
		if (!manifest) return;
		setApproving(true);
		try {
			await manifestService.approve(manifest.id, buildUpdateMerges());
			notifySuccess('Manifest approved successfully');
			onComplete();
		} catch (e: any) {
			notifyError(e?.message || 'Failed to approve manifest');
		} finally {
			setApproving(false);
		}
	};

	const handleRemoveRecord = async (recordId: string) => {
		if (!eventId) return;
		try {
			await manifestService.bulkDeleteRecords(eventId, [recordId]);
			setReviewRecords(prev => prev.filter(r => r.id !== recordId));
			setAllBatchRecords(prev => prev.filter(r => r.id !== recordId));
			notifySuccess('Record removed');
		} catch (e: any) {
			notifyError(e?.message || 'Failed to remove record');
		}
	};

	// ── Render steps ──

	const renderEventStep = () => (
		<div className="space-y-6">
			<div>
				<label className="block text-sm font-medium mb-2">Manifest Batch Name</label>
				<Input
					value={manifestName}
					onChange={(e) => setManifestName(e.target.value)}
					placeholder="e.g., Client Name - Event Flight Manifests"
				/>
			</div>

			<div>
				<label className="block text-sm font-medium mb-1">
					Destination airport(s) <span className="text-red-500">*</span>
				</label>
				<p className="text-sm text-gray-600 mb-2">
					The airport(s) attendees fly into for this program. Extraction anchors the correct inbound/outbound
					flights on these codes. Add more than one for metro areas (e.g. <span className="font-mono">JFK · EWR · LGA</span>).
				</p>
				<div className="flex flex-wrap items-center gap-2">
					{destinationAirports.map((code) => (
						<span
							key={code}
							className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-mono font-semibold pl-2.5 pr-1 py-0.5"
						>
							{code}
							<button
								type="button"
								onClick={() => removeDestinationAirport(code)}
								aria-label={`Remove ${code}`}
								className="rounded-full hover:bg-emerald-100 p-0.5"
							>
								<X className="w-3 h-3" />
							</button>
						</span>
					))}
					<div className="w-40">
						<AirportAutocomplete
							key={destInputKey}
							value=""
							onChange={addDestinationAirport}
							onSearch={(q) => manifestService.searchAirports(q).then((r) => r.map((a) => ({ iata: a.iata, name: a.name })))}
							placeholder="Add airport…"
						/>
					</div>
				</div>
				{destinationAirports.length === 0 && (
					<p className="text-xs text-amber-600 mt-2">Add at least one destination airport to continue.</p>
				)}
			</div>

			{preSelectedEventId ? (
				<div>
					<label className="block text-sm font-medium mb-2">Event</label>
					<div className="rounded-lg border bg-gray-50 p-4">
						<p className="font-medium">{selectedEvent?.name ?? '…'}</p>
						<p className="text-sm text-gray-500">{selectedEvent?.client_name ?? ''}</p>
					</div>
				</div>
			) : (
				<div>
					<label className="block text-sm font-medium mb-2">Select Event</label>
					<p className="text-sm text-gray-600 mb-4">Choose the event these flight manifests belong to.</p>

					{loading ? (
						<div className="flex justify-center py-8"><Spinner /></div>
					) : (
						<div className="space-y-3 max-h-64 overflow-y-auto">
							{events.map((event) => (
								<Card
									key={event.id}
									className={`cursor-pointer transition-all ${selectedEvent?.id === event.id ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:bg-gray-50'}`}
									onClick={() => handleEventSelect(event)}
								>
									<CardContent className="p-4">
										<div className="flex items-center justify-between">
											<div>
												<p className="font-medium">{event.name}</p>
												<p className="text-sm text-gray-500">{event.client_name}</p>
											</div>
											{selectedEvent?.id === event.id && <CheckCircle className="w-5 h-5 text-blue-500" />}
										</div>
									</CardContent>
								</Card>
							))}
							{events.length === 0 && (
								<p className="text-gray-500 text-center py-4">No events found. Create an event first.</p>
							)}
						</div>
					)}
				</div>
			)}

			<div className="flex justify-between pt-4">
				<Button variant="outline" onClick={onCancel}>Cancel</Button>
				<Button onClick={handleCreateAndAdvance} disabled={!selectedEvent || !manifestName.trim() || destinationAirports.length === 0 || loading}>
					{loading ? <Spinner className="w-4 h-4 mr-2" /> : null}
					Continue <ArrowRight className="w-4 h-4 ml-2" />
				</Button>
			</div>
		</div>
	);

	const renderUploadStep = () => (
		<div className="space-y-6">
			{/* Drop zone */}
			<div
				onDrop={handleDrop}
				onDragOver={handleDragOver}
				onClick={() => fileInputRef.current?.click()}
				className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors"
			>
				<Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
				<p className="text-lg font-medium text-gray-700 mb-1">Drag & drop PDF files here</p>
				<p className="text-sm text-gray-500">or click to browse</p>
				<input
					ref={fileInputRef}
					type="file"
					accept=".pdf,application/pdf"
					multiple
					className="hidden"
					onChange={(e) => handleFilesSelected(e.target.files)}
				/>
			</div>

			{/* File list */}
			{pendingFiles.length > 0 && (
				<div className="space-y-2">
					<h4 className="text-sm font-medium text-gray-700">{pendingFiles.length} file(s) selected</h4>
					{pendingFiles.map((pf, i) => (
						<div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
							<FileText className="w-5 h-5 text-gray-400 shrink-0" />
							<div className="flex-1 min-w-0">
								<p className="text-sm font-medium truncate">{pf.file.name}</p>
								<p className="text-xs text-gray-500">{(pf.file.size / 1024).toFixed(1)} KB</p>
							</div>
							{pf.status === 'pending' && (
								<button onClick={() => removePendingFile(i)} className="text-gray-400 hover:text-red-500">
									<X className="w-4 h-4" />
								</button>
							)}
							{pf.status === 'uploading' && <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />}
							{pf.status === 'done' && <CheckCircle className="w-4 h-4 text-green-500" />}
							{pf.status === 'error' && (
								<span className="text-xs text-red-500 flex items-center gap-1">
									<AlertTriangle className="w-4 h-4" /> {pf.error || 'Failed'}
								</span>
							)}
						</div>
					))}
				</div>
			)}

			{/* Actions */}
			<div className="flex justify-between pt-4">
				<Button variant="outline" onClick={() => setCurrentStep('event')}>
					<ArrowLeft className="w-4 h-4 mr-2" /> Back
				</Button>
				<div className="flex gap-3">
					{pendingFiles.some(f => f.status === 'pending') && (
						<Button onClick={handleUploadAll} disabled={uploading}>
							{uploading ? <Spinner className="w-4 h-4 mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
							Upload All
						</Button>
					)}
					{canProceedToProcessing && (
						<Button onClick={handleStartProcessing} disabled={uploading}>
							{uploading ? <Spinner className="w-4 h-4 mr-2" /> : null}
							Process {uploadedCount} PDF(s) <ArrowRight className="w-4 h-4 ml-2" />
						</Button>
					)}
				</div>
			</div>
		</div>
	);

	const renderProcessingStep = () => (
		<div className="space-y-6">
			<div className="text-center py-4">
				{isProcessing ? (
					<>
						<Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
						<h3 className="text-lg font-semibold mb-1">Processing PDFs...</h3>
						<p className="text-gray-600">Extracting flight information using AI. This may take a minute.</p>
					</>
				) : processingDone ? (
					<>
						<CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
						<h3 className="text-lg font-semibold mb-1">Processing Complete</h3>
						<p className="text-gray-600">
							{processingStatus?.summary.extracted || 0} file(s) extracted
							{processingStatus?.summary.failed ? `, ${processingStatus.summary.failed} failed` : ''}
						</p>
					</>
				) : null}
			</div>

			{/* Per-file status */}
			{processingStatus?.files && (
				<div className="space-y-2">
					{processingStatus.files.map((f) => (
						<div key={f.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
							<FileText className="w-5 h-5 text-gray-400 shrink-0" />
							<p className="flex-1 text-sm font-medium truncate">{f.filename}</p>
							<Badge variant={
								f.status === 'extracted' ? 'success' :
								f.status === 'needs_review' ? 'warning' :
								f.status === 'processing' ? 'warning' :
								f.status === 'failed' ? 'error' :
								'secondary'
							}>
								{f.status === 'needs_review' ? 'needs review' : f.status}
							</Badge>
							{/* Only finished-with-issue statuses explain themselves — a stale error on a
							    healthy 'extracted' file must not render as a phantom failure. */}
							{f.error && f.status === 'failed' && <span className="text-xs text-red-500">{f.error}</span>}
							{f.error && f.status === 'needs_review' && <span className="text-xs text-amber-600">{f.error}</span>}
						</div>
					))}
				</div>
			)}

			{/* Actions */}
			<div className="flex justify-end pt-4">
				{processingDone && (
					<Button onClick={handleAdvanceFromProcessing}>
						{reviewLoading ? <Spinner className="w-4 h-4 mr-2" /> : null}
						Review Extracted Data <ArrowRight className="w-4 h-4 ml-2" />
					</Button>
				)}
			</div>
		</div>
	);


	const rdPassengerGroups = useMemo(() => groupRecordsByPassenger(reviewDataRecords), [reviewDataRecords]);

	// Collect IATA codes for tooltip lookup
	const rdAirportCodes = useMemo(() => {
		const codes: string[] = [];
		for (const r of reviewDataRecords) {
			if (r.outbound_departure_airport) codes.push(r.outbound_departure_airport);
			if (r.outbound_arrival_airport) codes.push(r.outbound_arrival_airport);
			if (r.return_departure_airport) codes.push(r.return_departure_airport);
			if (r.return_arrival_airport) codes.push(r.return_arrival_airport);
			const flights = (r as any).all_flights;
			if (Array.isArray(flights)) {
				for (const f of flights) {
					if (f.departure_airport) codes.push(f.departure_airport);
					if (f.arrival_airport) codes.push(f.arrival_airport);
				}
			}
		}
		return [...new Set(codes)];
	}, [reviewDataRecords]);
	const rdAirportNames = useAirportNames(rdAirportCodes, manifestService);

	const renderReviewDataStep = () => {
		if (reviewDataLoading) {
			return (
				<div className="text-center py-12">
					<Loader2 className="w-10 h-10 text-blue-500 animate-spin mx-auto mb-4" />
					<p className="text-gray-600">Loading extracted data...</p>
				</div>
			);
		}

		const uniquePassengers = rdPassengerGroups.length;
		const totalRecords = reviewDataRecords.length;
		const allSourceFiles = new Set(rdAllBatchRecords.map(r => r.source_filename)).size;
		const totalExtracted = rdAllBatchRecords.length;
		const updatesCount = updateComparisons.filter(c => c.legs.some(l => l.type === 'updated' || l.type === 'new')).length;
		const newCount = totalRecords;
		const hasContent = totalExtracted > 0 || totalRecords > 0 || updatesCount > 0;

		// Records the extractor could not cleanly anchor on the destination — need manual verification.
		const mismatchRecords = new Map<string, RecordWithSourceDTO>();
		for (const r of [...rdAllBatchRecords, ...reviewDataRecords]) {
			if (r.extraction_flags?.destination_mismatch) mismatchRecords.set(r.id, r);
		}
		const destinationMismatchCount = mismatchRecords.size;

		const toggleLeg = (pendingId: string, legIdx: number) => {
			setAcceptedLegs(prev => {
				const current = new Set(prev[pendingId] || []);
				if (current.has(legIdx)) current.delete(legIdx); else current.add(legIdx);
				return { ...prev, [pendingId]: current };
			});
		};
		const acceptAll = (pendingId: string, legs: import('@/services/FlightManifestService').LegComparisonDTO[]) => {
			setAcceptedLegs(prev => ({
				...prev,
				[pendingId]: new Set(legs.map((l, i) => (l.type === 'updated' || l.type === 'new') ? i : -1).filter(i => i >= 0)),
			}));
		};
		const skipAll = (pendingId: string) => {
			setAcceptedLegs(prev => ({ ...prev, [pendingId]: new Set<number>() }));
		};

		const fmtDate = (d: string | null) => {
			if (!d) return '—';
			const [y, m, dd] = d.split('-');
			return `${dd}/${m}/${y}`;
		};

		return (
			<div className="space-y-6">
				<div className="text-center mb-2">
					<h3 className="text-lg font-semibold mb-1">Review Extracted Data</h3>
					<p className="text-sm text-gray-600">
						{hasContent
							? <>{totalExtracted} extracted from {allSourceFiles} source file{allSourceFiles !== 1 ? 's' : ''}.{updatesCount > 0 ? ` ${updatesCount} existing passenger${updatesCount !== 1 ? 's' : ''} with updates.` : ''}{newCount > 0 ? ` ${newCount} new.` : ''} {newCount > 0 ? 'Edit any field by clicking on it.' : ''}</>
							: <>No records could be extracted from this batch.</>
						}
					</p>
				</div>

				{/* Stats row */}
				<div className={`grid gap-4 ${updatesCount > 0 ? 'grid-cols-4' : 'grid-cols-3'}`}>
					<div className="bg-blue-50 rounded-lg p-3 text-center">
						<div className="text-xl font-bold text-blue-700">{totalExtracted}</div>
						<div className="text-xs text-blue-600">Extracted</div>
					</div>
					{updatesCount > 0 && (
						<div className="bg-amber-50 rounded-lg p-3 text-center">
							<div className="text-xl font-bold text-amber-700">{updatesCount}</div>
							<div className="text-xs text-amber-600">Existing Updated</div>
						</div>
					)}
					<div className="bg-emerald-50 rounded-lg p-3 text-center">
						<div className="text-xl font-bold text-emerald-700">{uniquePassengers}</div>
						<div className="text-xs text-emerald-600">New Passengers</div>
					</div>
					<div className="bg-purple-50 rounded-lg p-3 text-center">
						<div className="text-xl font-bold text-purple-700">{allSourceFiles}</div>
						<div className="text-xs text-purple-600">Source Files</div>
					</div>
				</div>

				{/* Destination-mismatch banner — records that couldn't be anchored on the program destination */}
				{destinationMismatchCount > 0 && (
					<div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
						<AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
						<div className="text-sm text-amber-900">
							<span className="font-semibold">{destinationMismatchCount} record{destinationMismatchCount !== 1 ? 's' : ''} need{destinationMismatchCount === 1 ? 's' : ''} manual destination check.</span>{' '}
							Their flights don’t have a clean arrival into <em>and</em> departure from the program destination
							(open-jaw, one-way, or self-booked). Look for the <span className="font-semibold text-amber-800">⚠ Verify dest</span> badge,
							then correct the outbound/return airports inline — or leave blank for non-air travel.
						</div>
					</div>
				)}

				{/* Update comparison cards */}
				{updateComparisons.map(comp => {
					const accepted = acceptedLegs[comp.pendingRecordId] || new Set<number>();
					const updatedLegs = comp.legs.filter(l => l.type === 'updated');
					const newLegs = comp.legs.filter(l => l.type === 'new');
					const unchangedLegs = comp.legs.filter(l => l.type === 'unchanged');
					const existingOnlyLegs = comp.legs.filter(l => l.type === 'existing_only');

					const hasChanges = updatedLegs.length > 0 || newLegs.length > 0;
					const allLegs = [...unchangedLegs, ...existingOnlyLegs];

					// No changes — show compact informational card
					if (!hasChanges) {
						return (
							<div key={comp.pendingRecordId} className="border border-gray-200 rounded-lg overflow-hidden">
								<div className="bg-gray-50 px-4 py-2.5 flex items-center justify-between">
									<div className="flex items-center gap-2">
										<span className="text-green-600 text-xs font-semibold uppercase tracking-wider px-2 py-0.5 bg-green-50 rounded">Match</span>
										<span className="font-semibold text-sm">{comp.passengerName}</span>
										{comp.pnr && <span className="text-xs text-gray-500 font-mono">PNR: {comp.pnr}</span>}
									</div>
									<span className="text-xs text-gray-500">{allLegs.length} leg{allLegs.length !== 1 ? 's' : ''} — no changes</span>
								</div>
								<div className="px-4 py-2 text-xs text-gray-500">
									{allLegs.map((leg, i) => {
										const l = leg.existing!;
										return (
											<span key={i} className="inline-flex items-center gap-1.5 mr-4">
												<span className="font-mono text-emerald-600">{l.departure_airport} → {l.arrival_airport}</span>
												<span>{l.flight_number || ''}</span>
												<span>{fmtDate(l.date)}</span>
												<span>{l.departure_time || ''}-{l.arrival_time || ''}</span>
											</span>
										);
									})}
								</div>
							</div>
						);
					}

					// Has changes — show full card with actions
					return (
						<div key={comp.pendingRecordId} className="border border-amber-200 rounded-lg overflow-hidden">
							{/* Header */}
							<div className="bg-amber-50 px-4 py-3 flex items-center justify-between">
								<div className="flex items-center gap-2">
									<span className="text-amber-600 text-xs font-semibold uppercase tracking-wider px-2 py-0.5 bg-amber-100 rounded">Update</span>
									<span className="font-semibold text-sm">{comp.passengerName}</span>
									{comp.pnr && <span className="text-xs text-gray-500 font-mono">PNR: {comp.pnr}</span>}
								</div>
								<div className="flex items-center gap-2 text-xs">
									{updatedLegs.length > 0 && <span className="text-amber-700">{updatedLegs.length} changed</span>}
									{newLegs.length > 0 && <span className="text-blue-700">{newLegs.length} new</span>}
									{unchangedLegs.length > 0 && <span className="text-gray-500">{unchangedLegs.length} unchanged</span>}
								</div>
							</div>

							<div className="divide-y">
								{/* Updated legs */}
								{comp.legs.map((leg, i) => {
									if (leg.type === 'updated' && leg.existing && leg.incoming) {
										const isAccepted = accepted.has(i);
										return (
											<div key={i} className={`px-4 py-2.5 text-sm ${isAccepted ? 'bg-amber-50/40' : 'bg-white'}`}>
												<div className="flex items-center justify-between">
													<div className="flex items-center gap-2">
														<span className="text-[10px] font-semibold uppercase tracking-wider text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded">{leg.existing.direction}</span>
														<span className="font-mono text-xs font-medium text-emerald-700">{leg.existing.departure_airport} → {leg.existing.arrival_airport}</span>
													</div>
													<button onClick={() => toggleLeg(comp.pendingRecordId, i)}
														className={`text-xs px-2.5 py-1 rounded font-medium transition-colors ${isAccepted ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
														{isAccepted ? '✓ Accepted' : 'Skipped'}
													</button>
												</div>
												<div className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
													{leg.changes?.map(field => {
														const labels: Record<string, string> = { date: 'Date', flight_number: 'Flight', departure_time: 'Departure', arrival_time: 'Arrival' };
														const exVal = field === 'date' ? fmtDate((leg.existing as any)[field]) : (leg.existing as any)[field] || '—';
														const incVal = field === 'date' ? fmtDate((leg.incoming as any)[field]) : (leg.incoming as any)[field] || '—';
														return (
															<div key={field} className="flex items-center gap-1.5">
																<span className="text-gray-500 w-16">{labels[field] || field}:</span>
																<span className="text-red-500 line-through">{exVal}</span>
																<span className="text-gray-400">→</span>
																<span className="text-green-700 font-medium">{incVal}</span>
															</div>
														);
													})}
												</div>
											</div>
										);
									}
									if (leg.type === 'new' && leg.incoming) {
										const isAccepted = accepted.has(i);
										return (
											<div key={i} className={`px-4 py-2.5 text-sm ${isAccepted ? 'bg-blue-50/40' : 'bg-white'}`}>
												<div className="flex items-center justify-between">
													<div className="flex items-center gap-2">
														<span className="text-[10px] font-semibold uppercase tracking-wider text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">new {leg.incoming.direction}</span>
														<span className="font-mono text-xs font-medium text-emerald-700">{leg.incoming.departure_airport} → {leg.incoming.arrival_airport}</span>
														<span className="text-gray-500">{leg.incoming.flight_number || ''}</span>
														<span className="text-gray-500">{fmtDate(leg.incoming.date)}</span>
														<span className="text-gray-500">{leg.incoming.departure_time || ''} - {leg.incoming.arrival_time || ''}</span>
													</div>
													<button onClick={() => toggleLeg(comp.pendingRecordId, i)}
														className={`text-xs px-2.5 py-1 rounded font-medium transition-colors ${isAccepted ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
														{isAccepted ? '✓ Accepted' : 'Skipped'}
													</button>
												</div>
											</div>
										);
									}
									return null;
								})}

								{/* Unchanged + existing-only legs (collapsed) */}
								{allLegs.length > 0 && (
									<div className="px-4 py-2 bg-gray-50/50 text-xs text-gray-500">
										<div className="font-medium text-gray-600 mb-1">Existing legs (no changes):</div>
										{allLegs.map((leg, i) => {
											const l = leg.existing!;
											return (
												<div key={i} className="inline-flex items-center gap-1.5 mr-4">
													<span className="font-mono text-emerald-600">{l.departure_airport} → {l.arrival_airport}</span>
													<span>{l.flight_number || ''}</span>
													<span>{fmtDate(l.date)}</span>
													<span>{l.departure_time || ''}-{l.arrival_time || ''}</span>
												</div>
											);
										})}
									</div>
								)}
							</div>

							{/* Footer actions — context-aware */}
							{(() => {
								const changeableIndices = comp.legs.map((l, i) => (l.type === 'updated' || l.type === 'new') ? i : -1).filter(i => i >= 0);
								const allAccepted = changeableIndices.every(i => accepted.has(i));
								const noneAccepted = changeableIndices.every(i => !accepted.has(i));
								return (
									<div className="bg-gray-50 px-4 py-2 flex items-center justify-between">
										<span className={`text-xs font-medium ${allAccepted ? 'text-green-600' : noneAccepted ? 'text-gray-400' : 'text-amber-600'}`}>
											{allAccepted ? `✓ All ${changeableIndices.length} change${changeableIndices.length !== 1 ? 's' : ''} accepted`
												: noneAccepted ? `All changes skipped`
												: `${changeableIndices.filter(i => accepted.has(i)).length} of ${changeableIndices.length} accepted`}
										</span>
										<div className="flex gap-2">
											{!noneAccepted && (
												<button onClick={() => skipAll(comp.pendingRecordId)} className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1">Skip All</button>
											)}
											{!allAccepted && (
												<button onClick={() => acceptAll(comp.pendingRecordId, comp.legs)} className="text-xs text-green-600 hover:text-green-800 px-2 py-1 font-medium">Accept All Changes</button>
											)}
										</div>
									</div>
								);
							})()}
						</div>
					);
				})}

				{/* All-matched banner: everything extracted matched existing records */}
				{totalExtracted > 0 && newCount === 0 && updatesCount === 0 && (
					<div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
						<CheckCircle className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
						<div>
							<p className="font-medium text-blue-800">All records matched existing data</p>
							<p className="text-sm text-blue-600 mt-1">
								{totalExtracted} record{totalExtracted !== 1 ? 's were' : ' was'} extracted from {allSourceFiles} source file{allSourceFiles !== 1 ? 's' : ''}, but {totalExtracted === 1 ? 'it matches' : 'they all match'} records already in the database with no changes detected.
							</p>
						</div>
					</div>
				)}

				{/* Editable table */}
				{reviewDataRecords.length > 0 ? (
					<div className="border rounded-lg overflow-hidden">
						<div className="overflow-x-auto max-h-[500px] overflow-y-auto">
							<table className="w-full text-sm min-w-[1000px] table-fixed">
								<colgroup>
									<col style={{ width: '48px' }} />
									<col style={{ width: '19%' }} />
									<col style={{ width: '13%' }} />
									<col style={{ width: '9%' }} />
									<col style={{ width: '11%' }} />
									<col style={{ width: '8%' }} />
									<col style={{ width: '8%' }} />
									<col style={{ width: '10%' }} />
									<col style={{ width: '13%' }} />
									<col style={{ width: '32px' }} />
								</colgroup>
								<thead className="bg-gray-50 sticky top-0 z-10">
									<tr className="border-b">
										<th className="text-left p-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Status</th>
										<th className="text-left p-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Passenger</th>
										<th className="text-left p-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Route</th>
										<th className="text-left p-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Flight</th>
										<th className="text-left p-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Date</th>
										<th className="text-left p-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Depart</th>
										<th className="text-left p-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Arrive</th>
										<th className="text-left p-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Booking Ref</th>
										<th className="text-left p-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Source</th>
										<th className="p-1" />
									</tr>
								</thead>
								<tbody>
									{rdPassengerGroups.map(group => (
										<PassengerGroupRows
											key={group.key}
											group={group}
											editingCell={rdEditingCell}
											editValue={rdEditValue}
											saving={rdSaving}
											fileLoading={rdFileLoading}
											manifestService={manifestService}
											onStartEdit={rdStartEdit}
											onCancelEdit={rdCancelEdit}
											onSaveEdit={rdSaveEdit}
											onEditValueChange={setRdEditValue}
											onViewSource={rdHandleViewSource}
											onDeletePassenger={rdRequestDeletePassenger}
											onDeleteBooking={rdRequestDeleteBooking}
											onDeleteLeg={rdRequestDeleteLeg}
											addingLeg={rdAddingLeg}
											newLeg={rdNewLeg}
											onAddLeg={rdStartAddLeg}
											onCancelAddLeg={rdCancelAddLeg}
											onSaveNewLeg={rdSaveNewLeg}
											onNewLegChange={setRdNewLeg}
											airportNames={rdAirportNames}
										/>
									))}
								</tbody>
							</table>
						</div>
					</div>
				) : totalExtracted === 0 ? (
					<div className="text-center py-8 text-gray-500">
						<p>No records could be extracted. Check the source PDFs and try again.</p>
					</div>
				) : null}

				{/* Delete confirmation */}
				{rdDeleteConfirm && (
					<ConfirmDialog
						isOpen={!!rdDeleteConfirm}
						title={`Delete ${rdDeleteConfirm.type}`}
						description={`Are you sure you want to delete ${rdDeleteConfirm.label}? This action cannot be undone.`}
						confirmText="Delete"
						variant="danger"
						isLoading={rdDeleting}
						onConfirm={rdHandleDeleteConfirm}
						onClose={() => setRdDeleteConfirm(null)}
					/>
				)}

				{/* Duplicate detection results (LLM call 1) */}
				{duplicatesLoading && (
					<div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-3">
						<Loader2 className="w-4 h-4 text-blue-500 animate-spin shrink-0" />
						<p className="text-sm text-blue-700">Checking for duplicates and matches against existing records...</p>
					</div>
				)}
				{!duplicatesLoading && duplicateAdvisories.length > 0 && (
					<div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
						<div className="flex items-center gap-3 mb-2">
							<AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
							<span className="text-sm font-medium text-amber-800">
								{duplicateAdvisories.length} potential issue{duplicateAdvisories.length !== 1 ? 's' : ''} detected
							</span>
						</div>
						<div className="space-y-2">
							{duplicateAdvisories.map((a, i) => (
								<div key={i} className="bg-white rounded border border-amber-100 px-3 py-2 text-sm">
									<div className="flex items-center gap-2 mb-1">
										<span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${
											a.severity === 'high' ? 'bg-red-100 text-red-700 border-red-200'
											: a.severity === 'medium' ? 'bg-amber-100 text-amber-700 border-amber-200'
											: 'bg-blue-100 text-blue-700 border-blue-200'
										}`}>{a.severity}</span>
										<span className="font-medium text-gray-800">{ADVISORY_LABELS[a.type] || a.type}</span>
									</div>
									<p className="text-gray-600 text-xs">{a.message}</p>
								</div>
							))}
						</div>
					</div>
				)}
				{!duplicatesLoading && duplicateAdvisories.length === 0 && hasContent && (
					<div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-3">
						<CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
						<p className="text-sm text-green-700">No duplicates or matching issues detected.</p>
					</div>
				)}

				{/* File viewer modal */}
				{rdViewingFile && (
					<FileViewerModal
						isOpen={!!rdViewingFile}
						file={rdViewingFile}
						onClose={() => setRdViewingFile(null)}
					/>
				)}

				{/* Navigation */}
				<div className="flex justify-between pt-4 border-t">
					<Button variant="outline" onClick={() => setCurrentStep('processing')}>
						<ArrowLeft className="w-4 h-4 mr-2" /> Back
					</Button>
					{totalExtracted > 0 && newCount === 0 && updatesCount === 0 ? (
						<Button onClick={onComplete}>
							<CheckCircle className="w-4 h-4 mr-2" />
							Done — View Event Table
						</Button>
					) : (
						<Button onClick={handleAdvanceFromReviewData} disabled={(totalExtracted === 0 && updatesCount === 0) || reviewLoading}>
							{reviewLoading ? <Spinner className="w-4 h-4 mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
							Review & Approve
						</Button>
					)}
				</div>
			</div>
		);
	};

	const ADVISORY_LABELS: Record<string, string> = {
		potential_duplicate: 'Duplicate',
		potential_update: 'Schedule Update',
		open_jaw: 'Open Jaw',
		schedule_conflict: 'Schedule Conflict',
		missing_return: 'Missing Return',
		missing_flights: 'No Flights Extracted',
		no_data: 'No Data Extracted',
		unmatched_passenger: 'Unmatched',
		name_mismatch: 'Name Mismatch',
		other: 'Other',
	};

	const severityConfig = {
		high: { badge: 'bg-red-100 text-red-700 border-red-200' },
		medium: { badge: 'bg-amber-100 text-amber-700 border-amber-200' },
		low: { badge: 'bg-blue-100 text-blue-700 border-blue-200' },
	};

	// Build lookup: record ID → anomalies affecting that record
	const anomaliesByRecord = useMemo(() => {
		const map = new Map<string, AdvisoryDTO[]>();
		for (const a of advisories) {
			for (const rid of a.record_ids) {
				if (!map.has(rid)) map.set(rid, []);
				map.get(rid)!.push(a);
			}
		}
		return map;
	}, [advisories]);

	const [removeConfirm, setRemoveConfirm] = useState<string | null>(null);
	const [anomalyModal, setAnomalyModal] = useState<{ recordId: string; anomalies: AdvisoryDTO[] } | null>(null);
	// Per-record status: 'pending' (default) or 'approved'
	const [recordStatuses, setRecordStatuses] = useState<Record<string, 'pending' | 'approved'>>({});

	const setRecordStatus = (recordId: string, status: 'pending' | 'approved') => {
		setRecordStatuses(prev => ({ ...prev, [recordId]: status }));
	};

	const setAllRecordStatuses = (status: 'approved') => {
		const updated: Record<string, 'pending' | 'approved'> = {};
		for (const r of reviewRecords) {
			updated[r.id] = status;
		}
		setRecordStatuses(updated);
	};

	// Computed counts for bulk action labels
	const statusCounts = useMemo(() => {
		let approved = 0, pending = 0;
		for (const r of reviewRecords) {
			const s = recordStatuses[r.id] || 'pending';
			if (s === 'approved') approved++;
			else pending++;
		}
		return { approved, pending };
	}, [reviewRecords, recordStatuses]);

	const renderReviewApproveStep = () => {
		// Build a lookup from record ID to passenger name for display
		const allKnownRecords = [...reviewDataRecords, ...rdAllBatchRecords];
		const recordNameMap = new Map<string, string>();
		for (const r of allKnownRecords) {
			const name = [r.passenger_first_name, r.passenger_last_name].filter(Boolean).join(' ');
			if (name) recordNameMap.set(r.id, name);
		}

		const uniquePassengers = new Set(reviewRecords.map(r => r.passenger_name).filter(Boolean)).size;
		const totalExtracted = allBatchRecords.length;
		const highCount = advisories.filter(a => a.severity === 'high').length;
		const medCount = advisories.filter(a => a.severity === 'medium').length;
		const lowCount = advisories.filter(a => a.severity === 'low').length;
		const totalAnomalies = advisories.length;

		if (reviewLoading || advisoriesLoading) {
			return (
				<div className="text-center py-12">
					<Loader2 className="w-10 h-10 text-blue-500 animate-spin mx-auto mb-4" />
					<p className="text-gray-600">Loading review data and checking for anomalies...</p>
				</div>
			);
		}

		return (
			<div className="space-y-6">
				{/* Header */}
				<div className="text-center mb-2">
					<h3 className="text-lg font-semibold mb-1">Review & Approve</h3>
					<p className="text-sm text-gray-600">
						{reviewRecords.length} record{reviewRecords.length !== 1 ? 's' : ''} ready for review.
						{totalAnomalies > 0 ? ` ${totalAnomalies} anomal${totalAnomalies !== 1 ? 'ies' : 'y'} detected.` : ' No anomalies detected.'}
					</p>
				</div>

				{/* Summary stats */}
				<div className={`grid gap-3 ${totalAnomalies > 0 ? 'grid-cols-4' : 'grid-cols-3'}`}>
					<div className="bg-blue-50 rounded-lg p-3 text-center">
						<div className="text-xl font-bold text-blue-700">{totalExtracted}</div>
						<div className="text-xs text-blue-600">Extracted</div>
					</div>
					<div className="bg-emerald-50 rounded-lg p-3 text-center">
						<div className="text-xl font-bold text-emerald-700">{uniquePassengers}</div>
						<div className="text-xs text-emerald-600">Passengers</div>
					</div>
					<div className="bg-green-50 rounded-lg p-3 text-center">
						<div className="text-xl font-bold text-green-700">{reviewRecords.length}</div>
						<div className="text-xs text-green-600">To Approve</div>
					</div>
					{totalAnomalies > 0 && (
						<div className="bg-amber-50 rounded-lg p-3 text-center">
							<div className="text-xl font-bold text-amber-700">{totalAnomalies}</div>
							<div className="text-xs text-amber-600">Anomalies</div>
						</div>
					)}
				</div>

				{/* Anomaly summary banner */}
				{totalAnomalies === 0 ? (
					<div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-3">
						<CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
						<p className="text-sm text-green-700">No anomalies detected — records look good.</p>
						<Button variant="outline" size="sm" className="ml-auto text-xs" onClick={handleReAnalyze} disabled={advisoriesLoading}>
							{advisoriesLoading ? <Spinner className="w-3 h-3 mr-1" /> : <Search className="w-3 h-3 mr-1" />}
							Re-check
						</Button>
					</div>
				) : (
					<div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
						<div className="flex items-center gap-3 mb-2">
							<AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
							<span className="text-sm font-medium text-amber-800">Anomalies found — review flagged rows below</span>
							<div className="flex gap-2 ml-auto items-center">
								{highCount > 0 && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">{highCount} High</span>}
								{medCount > 0 && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{medCount} Med</span>}
								{lowCount > 0 && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{lowCount} Low</span>}
								<Button variant="outline" size="sm" className="text-xs" onClick={handleReAnalyze} disabled={advisoriesLoading}>
									{advisoriesLoading ? <Spinner className="w-3 h-3 mr-1" /> : <Search className="w-3 h-3 mr-1" />}
									Re-check
								</Button>
							</div>
						</div>
					</div>
				)}

				{/* Records table */}
				{reviewRecords.length > 0 ? (
					<div className="border rounded-lg overflow-hidden">
						<div className="overflow-x-auto max-h-[440px] overflow-y-auto">
							<table className="w-full text-sm min-w-[900px] table-fixed">
								<colgroup>
									<col style={{ width: '36px' }} />
									<col style={{ width: '18%' }} />
									<col style={{ width: '10%' }} />
									<col style={{ width: '10%' }} />
									<col style={{ width: '12%' }} />
									<col style={{ width: '10%' }} />
									<col style={{ width: '8%' }} />
									<col style={{ width: '12%' }} />
									<col style={{ width: '150px' }} />
								</colgroup>
								<thead className="bg-gray-50 sticky top-0 z-10">
									<tr className="border-b">
										<th className="p-2 text-center text-[11px] font-semibold uppercase text-gray-500">#</th>
										<th className="text-left p-2 text-[11px] font-semibold uppercase text-gray-500">Passenger</th>
										<th className="text-center p-2 text-[11px] font-semibold uppercase text-gray-500">Anomalies</th>
										<th className="text-left p-2 text-[11px] font-semibold uppercase text-gray-500">PNR</th>
										<th className="text-left p-2 text-[11px] font-semibold uppercase text-gray-500">Route</th>
										<th className="text-left p-2 text-[11px] font-semibold uppercase text-gray-500">Date</th>
										<th className="text-left p-2 text-[11px] font-semibold uppercase text-gray-500">Flight</th>
										<th className="text-left p-2 text-[11px] font-semibold uppercase text-gray-500">Source</th>
										<th className="p-2 text-center text-[11px] font-semibold uppercase text-gray-500">Actions</th>
									</tr>
								</thead>
								<tbody>
									{reviewRecords.map((r, i) => {
										const recordAnomalies = anomaliesByRecord.get(r.id) || [];
										const hasAnomalies = recordAnomalies.length > 0;
										const status = recordStatuses[r.id] || 'pending';
										const rowBg = status === 'approved' ? 'bg-green-50/60'
											: hasAnomalies ? 'bg-red-50/30'
											: '';

										return (
											<tr key={r.id} className={`border-b hover:bg-gray-50/50 ${rowBg}`}>
												<td className="p-2 text-center text-xs text-gray-400">{i + 1}</td>
												<td className="p-2">
													<span className="font-medium whitespace-nowrap truncate block">
														{r.passenger_first_name || r.passenger_last_name
															? `${r.passenger_first_name || ''} ${r.passenger_last_name || ''}`.trim()
															: <span className="text-red-500 italic">Missing</span>
														}
													</span>
												</td>
												{/* Anomalies column */}
												<td className="p-2 text-center">
													{hasAnomalies ? (
														<button
															onClick={() => setAnomalyModal({ recordId: r.id, anomalies: recordAnomalies })}
															className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded border cursor-pointer hover:opacity-80 transition-opacity bg-amber-100 text-amber-700 border-amber-200"
														>
															<AlertTriangle className="w-3 h-3" />
															{recordAnomalies.length > 1 ? `${recordAnomalies.length} issues` : ADVISORY_LABELS[recordAnomalies[0].type] || recordAnomalies[0].type}
														</button>
													) : (
														<span className="text-green-500 text-xs">✓</span>
													)}
												</td>
												<td className="p-2 font-mono text-xs truncate">{r.primary_pnr || '—'}</td>
												<td className="p-2">
													{r.outbound_departure_airport && r.outbound_arrival_airport
														? <span className="font-mono text-xs text-emerald-700">{r.outbound_departure_airport} → {r.outbound_arrival_airport}</span>
														: '—'
													}
												</td>
												<td className="p-2 text-xs text-gray-600 whitespace-nowrap">{formatDate(r.outbound_departure_date)}</td>
												<td className="p-2 text-xs">{r.outbound_flight_number || '—'}</td>
												<td className="p-2 text-xs truncate" title={r.source_filename}>
													{r.file_id ? (
														<button
															onClick={() => raHandleViewSource(r.file_id, r.source_filename || 'document.pdf')}
															className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1 truncate"
															disabled={raFileLoading === r.file_id}
														>
															{raFileLoading === r.file_id ? <Loader2 className="w-3 h-3 animate-spin shrink-0" /> : <Eye className="w-3 h-3 shrink-0" />}
															<span className="truncate">{r.source_filename}</span>
														</button>
													) : (
														<span className="text-gray-500">{r.source_filename}</span>
													)}
												</td>
												{/* Per-row actions */}
												<td className="p-2">
													<div className="flex items-center justify-center gap-1">
														{status === 'approved' && (
															<span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-green-100 text-green-700">
																Approved
															</span>
														)}
														<button
															onClick={() => setRecordStatus(r.id, status === 'approved' ? 'pending' : 'approved')}
															className={`p-1 rounded transition-colors ${status === 'approved' ? 'text-green-600 bg-green-100 ring-1 ring-green-300' : 'text-gray-400 hover:text-green-600 hover:bg-green-50'}`}
															title={status === 'approved' ? 'Undo approve' : 'Approve'}
														>
															<CheckCircle className="w-3.5 h-3.5" />
														</button>
														<button
															onClick={() => setRemoveConfirm(r.id)}
															className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
															title="Remove"
														>
															<Trash2 className="w-3.5 h-3.5" />
														</button>
													</div>
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					</div>
				) : (
					<div className="text-center py-8">
						<div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
							<AlertTriangle className="w-8 h-8 text-gray-400" />
						</div>
						<h4 className="text-lg font-semibold text-gray-800 mb-2">No Records to Approve</h4>
						<p className="text-sm text-gray-500 max-w-md mx-auto">No records were extracted from this upload.</p>
					</div>
				)}

				{/* Remove confirmation */}
				<ConfirmDialog
					isOpen={!!removeConfirm}
					title="Remove Record"
					description="Are you sure you want to remove this record? This cannot be undone."
					confirmText="Remove"
					variant="danger"
					onConfirm={() => { if (removeConfirm) handleRemoveRecord(removeConfirm); setRemoveConfirm(null); }}
					onClose={() => setRemoveConfirm(null)}
				/>

				{/* Anomaly detail modal */}
				{anomalyModal && (() => {
					const record = reviewRecords.find(r => r.id === anomalyModal.recordId);
					const passengerName = record
						? [record.passenger_first_name, record.passenger_last_name].filter(Boolean).join(' ') || 'Unknown'
						: 'Unknown';

					return (
						<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setAnomalyModal(null)}>
							<div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
								<div className="px-6 py-4 border-b flex items-center justify-between">
									<div>
										<h3 className="text-lg font-semibold">Anomalies — {passengerName}</h3>
										<p className="text-sm text-gray-500">{anomalyModal.anomalies.length} issue{anomalyModal.anomalies.length !== 1 ? 's' : ''} detected</p>
									</div>
									<button onClick={() => setAnomalyModal(null)} className="text-gray-400 hover:text-gray-600 p-1">
										<X className="w-5 h-5" />
									</button>
								</div>
								<div className="p-6 space-y-4">
									{anomalyModal.anomalies.map((a, ai) => {
										const config = severityConfig[a.severity] || severityConfig.low;
										const label = ADVISORY_LABELS[a.type] || a.type;

										return (
											<div key={ai} className="border rounded-lg overflow-hidden">
												<div className="px-4 py-3">
													<div className="flex items-center gap-2 mb-2">
														<span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${config.badge}`}>
															{a.severity}
														</span>
														<span className="text-sm font-semibold text-gray-800">{label}</span>
													</div>
													<p className="text-sm text-gray-700 mb-3">{a.message}</p>
													{a.metadata?.details && (
														<p className="text-xs text-gray-500 mb-2">{a.metadata.details}</p>
													)}
													{a.metadata?.airports && (
														<p className="text-xs text-gray-500 mb-2">Airports: {a.metadata.airports}</p>
													)}
													<div className="bg-gray-50 rounded-lg p-3 mt-2">
														<p className="text-xs text-gray-500 italic">
															No automated resolution available for this anomaly type yet. Review the details above and use the approve or remove actions on the record as needed.
														</p>
													</div>
												</div>
											</div>
										);
									})}
								</div>
								<div className="px-6 py-4 border-t flex justify-end">
									<Button variant="outline" onClick={() => setAnomalyModal(null)}>Close</Button>
								</div>
							</div>
						</div>
					);
				})()}

				{/* Status summary bar */}
				{statusCounts.approved > 0 && (
					<div className="bg-gray-50 border rounded-lg px-4 py-2 flex items-center gap-4 text-xs">
						<span className="text-gray-500 font-medium">Status:</span>
						<span className="flex items-center gap-1 text-green-700">
							<CheckCircle className="w-3 h-3" />
							{statusCounts.approved} approved
						</span>
						{statusCounts.pending > 0 && (
							<span className="ml-auto text-gray-400">{statusCounts.pending} remaining</span>
						)}
					</div>
				)}

				{/* PDF viewer modal */}
				{raViewingFile && (
					<FileViewerModal
						isOpen={!!raViewingFile}
						file={raViewingFile}
						onClose={() => setRaViewingFile(null)}
					/>
				)}

				{/* Navigation + bulk actions */}
				<div className="flex justify-between pt-4 border-t">
					<Button variant="outline" onClick={() => setCurrentStep('review_data')}>
						<ArrowLeft className="w-4 h-4 mr-2" /> Back to Review Data
					</Button>
					<div className="flex gap-3">
						<Button
							variant="outline"
							onClick={() => { setAllRecordStatuses('approved'); }}
							disabled={approving || reviewRecords.length === 0 || statusCounts.approved === reviewRecords.length}
						>
							<CheckCircle className="w-4 h-4 mr-2" />
							{statusCounts.approved === reviewRecords.length ? 'All Approved' : `Approve All (${reviewRecords.length})`}
						</Button>
						<Button
							onClick={handleApproveAndFinish}
							disabled={approving || reviewRecords.length === 0 || statusCounts.pending > 0}
						>
							{approving ? <Spinner className="w-4 h-4 mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
							Submit — Approve {reviewRecords.length} Records
						</Button>
					</div>
				</div>
			</div>
		);
	};

	return (
		<PageContainer>
			<PageHeader
				title="New Flight Manifest Batch"
				description="Upload flight manifest PDFs and extract passenger data"
			>
				<Button variant="outline" onClick={onCancel}>Cancel</Button>
			</PageHeader>

			<Card className="mb-6">
				<CardContent className="pt-6">
					<Steps steps={steps} />
				</CardContent>
			</Card>

			<Card>
				<CardContent className="p-6">
					{currentStep === 'event' && renderEventStep()}
					{currentStep === 'upload' && renderUploadStep()}
					{currentStep === 'processing' && renderProcessingStep()}
					{currentStep === 'review_data' && renderReviewDataStep()}

					{currentStep === 'review_approve' && renderReviewApproveStep()}
				</CardContent>
			</Card>
		</PageContainer>
	);
}
